import React from 'react';
import { APITypes } from '../../../primary-record-types';
import useCareTeamMembers from '../../../hooks/api/useCareTeamMembers';
import useEvaluatedPermissions from '../../../hooks/api/useEvaluatedPermissions';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';

export type ProviderList =
    | APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails>[];

export type ProviderSupportList =
    | APITypes.medicalProfileService.SupportCareTeamMemberDetails<APITypes.medicalProfileService.SupportCareTeamContactDetails>[]
    | APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails>[];

export type RenderWithProvidersOwnProps = {
    medicalProfileId: string;
    includeSupportCareTeamMembers?: false;
    render: (providers: ProviderList) => React.ReactNode;
};

export type RenderWithProvidersSupportListOwnProps = {
    medicalProfileId: string;
    includeSupportCareTeamMembers: true;
    render: (providers: ProviderSupportList) => React.ReactNode;
};

export type RenderWithProvidersProps = RenderWithProvidersOwnProps | RenderWithProvidersSupportListOwnProps;

const RenderWithProviders: React.FC<RenderWithProvidersProps | RenderWithProvidersSupportListOwnProps> = (props) => {
    const { hasListPermission, isLoadingCareTeamMembers, careTeamMembers } = useCareTeamMembers({
        medicalProfileId: props.medicalProfileId,
    });
    // useHasPermission reports false while the permission query is still in flight; without
    // waiting on it the form would mount with an empty provider list, then unmount (losing
    // user input) once permissions resolve and the care-team request starts.
    const { isLoading: isLoadingPermissions } = useEvaluatedPermissions({
        medicalProfileId: props.medicalProfileId,
    });

    if (!hasListPermission)
        return (
            <AnimateLoadedContent isLoading={isLoadingPermissions || isLoadingCareTeamMembers}>
                {() => props.render([])}
            </AnimateLoadedContent>
        );

    if (props.includeSupportCareTeamMembers)
        return (
            <AnimateLoadedContent isLoading={isLoadingCareTeamMembers && hasListPermission}>
                {() =>
                    props.render(
                        careTeamMembers.filter(
                            (_) => _.careTeamMemberType === 'support' || _.careTeamMemberType === 'provider',
                        ) as ProviderSupportList,
                    )
                }
            </AnimateLoadedContent>
        );

    return (
        <AnimateLoadedContent isLoading={isLoadingCareTeamMembers && hasListPermission}>
            {() => props.render(careTeamMembers.filter((_) => _.careTeamMemberType === 'provider') as ProviderList)}
        </AnimateLoadedContent>
    );
};

export default React.memo(RenderWithProviders);
